<template>
  <div class="resource-container">
    <div class="resource-card">
      <div class="card-content">
        <div class="image-section">
          <img
            v-if="activity.thumbnail"
            :src="activity.thumbnail"
            :alt="activity.title"
            class="card-image"
          />
          <img
            v-else
            src="/images/content-placeholder.svg"
            :alt="activity.title || 'Placeholder'"
            class="card-image"
          />
        </div>
        <div class="content-section">
          <h2 class="resource-title">{{ activity.title }}</h2>
          <p class="resource-description">
            {{ activity.short_description || "No description available" }}
          </p>
          <div class="metadata-section">
            <div v-if="activity.grade" class="metadata-item">
              <p class="metadata-label">Grade:</p>
              <p class="metadata-value">{{ activity.grade }}</p>
            </div>
            <div v-if="activity.subject" class="metadata-item">
              <p class="metadata-label">Subject:</p>
              <p class="metadata-value">{{ activity.subject }}</p>
            </div>
            <div v-if="activity.duration" class="metadata-item">
              <p class="metadata-label">Duration:</p>
              <p class="metadata-value">{{ activity.duration }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ActionButton
      class="view-button"
      variant="primary"
      size="large"
      @click="viewResources(activity.id)"
      @mouseover="showTooltip = true"
      @mouseleave="showTooltip = false"
    >
      View Resources
    </ActionButton>
  </div>
</template>

<script setup>
import { defineProps, ref } from "vue";
import ActionButton from "../common/ActionButton.vue";
import { useRouter } from "vue-router";

defineProps({
  activity: Object,
});

const router = useRouter();
const showTooltip = ref(false);

function viewResources(id) {
  router.push({
    name: "MyMakerCartActivitiesPage",
    params: {
      id: id,
    },
  });
}
</script>

<style scoped>
.resource-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-end;
  position: relative;
}

.resource-card {
  border: 1px solid #d8dae5;
  border-radius: 16px;
  padding: 16px;
  width: 100%;
}

.card-content {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.image-section {
  flex-shrink: 0;
  width: 348px;
  height: 212px;
  border: 1px solid #d8dae5;
  border-radius: 16px;
  overflow: hidden;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.content-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
}

.resource-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xxl);
  line-height: 36px;
  color: var(--primary-blue);
  margin: 0;
  width: 100%;
}

.resource-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.5px;
  color: #3b4648;
  margin: 0;
  width: 100%;
}

.metadata-section {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  width: 100%;
  flex-wrap: wrap;
}

.metadata-item {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.metadata-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.014px;
  color: #e00069;
  margin: 0 0 0 0;
}

.metadata-value {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.035px;
  color: #e00069;
  margin: 0;
}

.view-button {
  width: 150px;
  height: 56px;
  padding: 10px 16px;
  border-radius: 12px;
}

.view-button :deep(.action-button) {
  width: 150px;
  height: 56px;
  padding: 10px 16px;
  border-radius: 12px;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
}

.tooltip {
  position: absolute;
  bottom: -60px;
  right: 0;
  background-color: #f0f0f0;
  border: 1px solid #cccccc;
  border-radius: 4px;
  padding: 10px;
  font-size: 12px;
  color: #3b4648;
  white-space: nowrap;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
  .card-content {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .image-section {
    width: 100%;
    height: 200px;
  }

  .metadata-section {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 8px;
  }

  .view-button {
    width: 100%;
  }

  .resource-title {
    font-size: var(--fs-large);
  }

  .resource-description {
    font-size: var(--fs-small);
  }
}
</style>
